import React, { useEffect, useState } from 'react';
import { usePortal } from '../../hooks/usePortal';
import Tooltip from '../tooltip/Tooltip';
import { debounce } from 'lodash';

type HighlightPosition = {
  top: number,
  left: number,
  width: number,
  height: number,
  boxShadow: string,
};

interface HighlighterInterface {
  query: string | null,
  message: React.ReactNode,
  padding?: number;
}

const Highlighter = ({ query, message, padding = 10 }: HighlighterInterface): JSX.Element => {

  const [ show, setShow ] = useState(false);
  const [ element, setElement ] = useState<Element | null>();
  const [ highlightPos, setHighlightPos ] = useState<HighlightPosition | null>(null);

  useEffect(() => {
    let id: any;
    if (query) {
      const getElement = () => {
        const el = document.querySelector(query);
        if (el) {
          clearInterval(id);
          setElement(el);
        }
      };
      id = setInterval(getElement, 50);
    } else {
      clearInterval(id);
    }
    return () => {
      clearInterval(id);
    };
  }, [ query ]);

  useEffect(() => {
    const highlightPosFn = debounce(() => setHighlightPos(calcHighlight()), 250);

    if (element) {
      highlightPosFn();

      window.addEventListener('scroll', highlightPosFn);

      setTimeout(() => {
        setShow(true);

        window.scrollTo({
          top: (element.getBoundingClientRect().top + window.scrollY) - (window.innerHeight * .4),
          behavior: 'smooth',
        });

      }, 500);

    } else {
      setHighlightPos(null);
      setShow(false);
    }
    return () => {
      window.removeEventListener('scroll', highlightPosFn);
    };

  }, [ element ]);

  const calcHighlight = (): HighlightPosition | null => {
    if (element) {
      const bounds = element.getBoundingClientRect();
      return ({
        top: window.scrollY + bounds.top - padding,
        left: bounds.left - padding,
        width: bounds.width + (padding * 2),
        height: bounds.height + (padding * 2),
        boxShadow: 'rgb(17, 17, 120) 0px 0px 0px 3px, rgba(0, 0, 0, 0.4) 0px 0px 0px 20000px',
      });
    }
    return null;
  };

  const highlighter = usePortal((
    <div className={'absolute w-full h-full top-0 left-0 z-max-1 pointer-events-none'}>
      <Tooltip
        visible
        tooltipMessage={<div className={'text-lg whitespace-pre-wrap'}>{message}</div>}
        placement='top-start'
        appendTo={'parent'}
        onClickOutside={() => setShow(false)}
      >
        <div className={'absolute pointer-events-none'} style={{ ...highlightPos }}></div>
      </Tooltip>
    </div>
  ));

  return show ? highlighter : <></>;
};

export default Highlighter;
